import { LayoutDashboardIcon, TrendingUpIcon } from 'lucide-react'

import { Button, SectionHeading } from '@/components/ui'
import { useToast } from '@/hooks'
import { type Project, useInsightsMutations } from '@/modules/projects'
import { semanticColors } from '@/utils'
import { AnomaliesPreview } from './AnomaliesPreview'
import { CategoriesPreview } from './CategoriesPreview'
import { IncomePreview } from './IncomePreview'
import { InsightsProgressPanel } from './InsightsProgressPanel'
import { MerchantsPreview } from './MerchantsPreview'
import { PreviewTile } from './PreviewTile'

interface InsightsGenerateSectionProps {
  project: Project
  isGenerating?: boolean
}

const InflowOutflowLegend = () => {
  return (
    <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
      <span className="inline-flex items-center gap-1">
        <span
          className="h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: semanticColors.inflow }}
        />
        Inflow
      </span>
      <span className="inline-flex items-center gap-1">
        <span
          className="h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: semanticColors.outflow }}
        />
        Outflow
      </span>
    </div>
  )
}

export function InsightsGenerateSection({
  project,
  isGenerating,
}: InsightsGenerateSectionProps) {
  const { successToast, errorToast } = useToast()
  const { createInsights } = useInsightsMutations()

  const handleGenerateInsights = () => {
    createInsights(project.slug)
      .then(() => successToast(`Insights generation started`))
      .catch((error) => errorToast(`Failed to generate insights`, error))
  }

  if (isGenerating) {
    return <InsightsProgressPanel />
  }

  return (
    <div className="max-w-6xl space-y-8">
      <SectionHeading
        className="flex-col sm:flex-row"
        title="Insights"
        description={
          <span className="block max-w-2xl text-pretty">
            Turn this project's transactions into an interactive dashboard:
            income trends over time, where money flows by category and merchant,
            and what's worth a second look.
          </span>
        }
        actions={
          <Button type="button" onClick={handleGenerateInsights}>
            <LayoutDashboardIcon />
            Generate Insights
          </Button>
        }
      />

      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <PreviewTile
            title="Income"
            description="Monthly and cumulative income trends across every year in the data."
            caption="Monthly income"
            captionRight={
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-[10px] text-emerald-700">
                <TrendingUpIcon className="h-2.5 w-2.5" strokeWidth={3} />
                +18% YoY
              </span>
            }
            preview={<IncomePreview />}
          />
          <PreviewTile
            title="Categories"
            description="Spending and inflows grouped into categories, with month-over-month breakdowns."
            caption="By category"
            captionRight={<InflowOutflowLegend />}
            preview={<CategoriesPreview />}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <PreviewTile
            title="Merchants"
            description="Transactions rolled up by merchant, with name variations bundled automatically."
            caption="Top merchants"
            preview={<MerchantsPreview />}
          />
          <PreviewTile
            title="Anomalies"
            description="Outliers, round-number amounts, and weekend activity."
            caption="Outliers"
            preview={<AnomaliesPreview />}
          />
        </div>
      </div>
    </div>
  )
}
